// Score credits given upon account creation
export const INITIAL_SCORE_CREDITS: number = 20;

export const BILLING_PERIOD_MONTHLY = "Monthly";
export const BILLING_PERIOD_YEARLY = "Yearly";
export const billingPeriods = [BILLING_PERIOD_MONTHLY, BILLING_PERIOD_YEARLY] as const;
export type BillingPeriod = typeof billingPeriods[number];

export interface Pricing {
  [BILLING_PERIOD_MONTHLY]: number;
  [BILLING_PERIOD_YEARLY]: number;
}

export interface ScoreCredits {
  [BILLING_PERIOD_MONTHLY]: number;
  [BILLING_PERIOD_YEARLY]: number;
}

// Basic plan
const Basic = {
  pricing: {
    [BILLING_PERIOD_MONTHLY]: 1,
    [BILLING_PERIOD_YEARLY]: 10,
  } as Pricing,
  scoreCredits: {
    [BILLING_PERIOD_MONTHLY]: 0,
    [BILLING_PERIOD_YEARLY]: 0,
  } as ScoreCredits,
} as const;

// Pro plan
const Pro = {
  pricing: {
    [BILLING_PERIOD_MONTHLY]: 5,
    [BILLING_PERIOD_YEARLY]: 50,
  } as Pricing,
  scoreCredits: {
    [BILLING_PERIOD_MONTHLY]: 10,
    [BILLING_PERIOD_YEARLY]: 120,
  } as ScoreCredits,
} as const;

// Ensemble plan
const Ensemble = {
  pricing: {
    [BILLING_PERIOD_MONTHLY]: 25,
    [BILLING_PERIOD_YEARLY]: 250,
  } as Pricing,
  scoreCredits: {
    [BILLING_PERIOD_MONTHLY]: 20,
    [BILLING_PERIOD_YEARLY]: 240,
  } as ScoreCredits,
  includedMembers: 40,
  extraSinger: {
    [BILLING_PERIOD_MONTHLY]: 1,
    [BILLING_PERIOD_YEARLY]: 8,
  },
} as const;

export const Plans = {
  Basic,
  Pro,
  Ensemble,
} as const;

export type PlanName = keyof typeof Plans;
export const planNames = Object.keys(Plans) as PlanName[];

// planId is something like Cantamus-Ensemble-EUR-Monthly
export function getBillingPeriod(planId: string): BillingPeriod {
  const parts = planId.split("-");
  const billingPeriod = parts[parts.length - 1];
  if (!billingPeriod || !isBillingPeriod(billingPeriod)) throw new Error(`Invalid billing period: ${billingPeriod}`);
  return billingPeriod as BillingPeriod;
}

// planId is something like Cantamus-Ensemble-EUR-Monthly
export function getPlan(planId: string) {
  const planName = getPlanName(planId);
  return Plans[planName];
}

// planId is something like Cantamus-Ensemble-EUR-Monthly
export function getPlanName(planId: string) {
  const planName = planNames.find((planName) => planId.includes(planName));
  if (!planName || !isPlanName(planName)) throw new Error(`Could not find a plan for planId "${planId}"`);
  return planName;
}

// Check is a string is a plan name (and return the correct PlanName type)
export function isBillingPeriod(billingPeriodName: string): billingPeriodName is BillingPeriod {
  return billingPeriods.find((billingPeriod) => billingPeriod === billingPeriodName) !== undefined;
}

// Check is a string is a plan name (and return the correct PlanName type)
export function isPlanName(planName: string): planName is PlanName {
  return planNames.find((name) => name === planName) !== undefined;
}

// 1 score credit = 1 euro
export function scoreCreditsPrice(scoreCredits: number) {
  const currentDate = new Date();
  const discountEndDate = new Date("2024-01-15 23:59:59");

  if (currentDate <= discountEndDate) {
    return scoreCredits / 2;
  }

  return scoreCredits;
}
